// Matching the server's problems to the places on screen that can show them.
//
// THE PATH IS THE SERVER'S AND IS NOT REBUILT HERE. A refusal arrives as `contacts[1].name`, which
// says field, row and column in one string; the client's only job is to put it where a person can
// read it. Deriving the position any other way — by field order, by index into the values — is how a
// message ends up under the wrong question, which is worse than not showing it at all.
//
// Extracted rather than left inline so the tests exercise what ships.

export type Problem = {Path: string; Code: string; Reason: string; Detail: string};

/**
 * Every problem that belongs to a field, INCLUDING the ones inside it.
 *
 * A composite field owns `contacts`, `contacts[1].name` and anything deeper. The form draws the
 * first against the field itself and hands the rest to the grid; a caller that wants only the
 * field's own uses `ownProblems`.
 */
export function problemsForField(problems: Problem[] | undefined, code: string): Problem[] {
    return (problems ?? []).filter(p =>
        p.Path === code || p.Path.startsWith(`${code}[`) || p.Path.startsWith(`${code}.`));
}

/** Only the problems about the field itself — not the ones about what is inside it. */
export function ownProblems(problems: Problem[] | undefined, code: string): Problem[] {
    return (problems ?? []).filter(p => p.Path === code);
}

/** Everything inside a repeating group: `contacts[1].name`, never plain `contacts`. */
export function nestedProblems(problems: Problem[] | undefined, code: string): Problem[] {
    return problemsForField(problems, code).filter(p => p.Path !== code);
}

/**
 * The problems for one cell of a repeating group.
 *
 * ANCHORED AT THE END, because the group is handed paths that still carry their full prefix and a
 * nested group would repeat the pattern deeper — `contacts[1].addresses[0].city`. Matching the tail
 * is what keeps a cell from claiming a problem belonging to a row inside it.
 */
export function problemsForCell(problems: Problem[] | undefined,
                                index: number, code: string): Problem[] {
    return (problems ?? []).filter(p => p.Path.endsWith(`[${index}].${code}`));
}
